/**
 * PATH: packages/email-hub/src/feature/pages/inbox-page/controller/useInboxPaneRuntime.ts
 * FILE_ID: EMAIL-INBOX-CONTROLLER-PANE-001
 * PURPOSE: Own pane layout, detail loading, markup, classification, and URL sync.
 */

/* eslint-disable react-hooks/exhaustive-deps -- Preserve pre-extraction hook dependencies. */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useEmail, type Email } from '../../../hooks/useEmails'
import { useEmailMobileExclusiveChrome } from '../../../hooks/useEmailMobileExclusiveChrome'
import { useGlobalMarkupShortcut } from '../../../hooks/useGlobalMarkupShortcut'
import { useIsNarrowViewport } from '../../../../_internal/hooks/useIsNarrowViewport'
import {
  EMAIL_LIST_WIDTH,
  useUiStore,
} from '../../../../_internal/stores/uiStore'
import {
  inboxFilterSignature,
  nextInboxFilterUrl,
  parseInboxFiltersFromUrl,
} from '../../inboxFilterUrlSync'
import type { useInboxDataRuntime } from './useInboxDataRuntime'
import type { useInboxState } from './useInboxState'

type InboxState = ReturnType<typeof useInboxState>
type InboxData = ReturnType<typeof useInboxDataRuntime>

export function useInboxPaneRuntime(state: InboxState, data: InboxData) {
  const isNarrow = useIsNarrowViewport()
  const inboxSide = useUiStore((store) => store.inboxSide)
  const listWidthPx = useUiStore((store) => store.listWidthPx)
  const setListWidthPx = useUiStore((store) => store.setListWidthPx)
  const readerExpanded = useUiStore((store) => store.readerExpanded)
  const toggleReaderExpanded = useUiStore((store) => store.toggleReaderExpanded)
  const [dragWidth, setDragWidth] = useState<number | null>(null)
  const twoPaneRef = useRef<HTMLDivElement>(null)
  const onSplitterPointerDown = useCallback(
    (event: React.PointerEvent) => {
      event.preventDefault()
      const container = twoPaneRef.current
      if (!container) return
      const rect = container.getBoundingClientRect()
      const measureFromRight = inboxSide === 'right'
      const move = (pointerEvent: PointerEvent) => {
        const raw = measureFromRight
          ? rect.right - pointerEvent.clientX
          : pointerEvent.clientX - rect.left
        setDragWidth(
          Math.min(
            EMAIL_LIST_WIDTH.max,
            Math.max(EMAIL_LIST_WIDTH.min, raw),
          ),
        )
      }
      const up = () => {
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', up)
        setDragWidth((width) => {
          if (width != null) setListWidthPx(width)
          return null
        })
      }
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', up)
    },
    [inboxSide, setListWidthPx],
  )
  const effectiveListWidth = dragWidth ?? listWidthPx
  const [markupSrc, setMarkupSrc] = useState<string | null>(null)
  const openMarkup = useCallback((src: string) => {
    setMarkupSrc((previous) => {
      if (
        previous
        && previous !== src
        && previous.startsWith('blob:')
      ) {
        URL.revokeObjectURL(previous)
      }
      return src
    })
  }, [])
  useGlobalMarkupShortcut({
    onOpen: openMarkup,
    onMissing: () => data.toast(
      'No image on the clipboard - copy or screenshot one first',
      'info',
    ),
  })
  const handleClassifyUnprocessed = useCallback(async () => {
    const limit = Math.max(1, Math.min(data.stats?.unprocessed ?? 200, 1000))
    const result = await data.runClassify(limit, false)
    if (!result) {
      data.toast(
        'Could not start AI classification - please try again.',
        'error',
      )
      return
    }
    data.toast(
      `Classifying ${result.queued} email${result.queued === 1 ? '' : 's'} in the background…`,
      'info',
    )
    data.refetch()
    data.refetchStats()
    window.setTimeout(() => {
      data.refetch()
      data.refetchStats()
    }, 8_000)
    window.setTimeout(() => {
      data.refetch()
      data.refetchStats()
    }, 25_000)
  }, [
    data.stats?.unprocessed,
    data.runClassify,
    data.refetch,
    data.refetchStats,
    data.toast,
  ])
  const handleRecategorize = useCallback(async () => {
    const result = await data.runClassify(1000, true, true)
    if (!result) {
      data.toast(
        'Could not start re-classification - please try again.',
        'error',
      )
      return
    }
    data.toast(
      `Re-classifying ${result.queued} email${result.queued === 1 ? '' : 's'} with the new rules…`,
      'info',
    )
    data.refetch()
    data.refetchStats()
    window.setTimeout(() => {
      data.refetch()
      data.refetchStats()
    }, 8_000)
    window.setTimeout(() => {
      data.refetch()
      data.refetchStats()
    }, 25_000)
  }, [
    data.runClassify,
    data.refetch,
    data.refetchStats,
    data.toast,
  ])
  const listEmail =
    data.emails.find((email) => email.id === state.selectedEmailId) || null
  const {
    email: detailEmail,
    bodyLoading: detailBodyLoading,
    error: detailFetchError,
    refetch: refetchDetail,
    patchLocal: patchDetailLocal,
  } = useEmail(state.selectedEmailId)
  const selectedEmail = detailEmail || listEmail
  const patchSelectedEmail = useCallback(
    (emailId: string, patch: Partial<Email>) => {
      data.updateEmailLocal(emailId, patch)
      if (emailId === state.selectedEmailId) {
        patchDetailLocal(patch)
      }
    },
    [data.updateEmailLocal, state.selectedEmailId, patchDetailLocal],
  )
  useEmailMobileExclusiveChrome(
    isNarrow && Boolean(selectedEmail || state.isComposeMode),
  )

  const lastWrittenFilterSigRef = useRef(
    inboxFilterSignature(state.searchParams),
  )
  useEffect(() => {
    state.setSearchParams(
      (previous) => {
        const next = nextInboxFilterUrl(previous, {
          selectedCategories: state.selectedCategories,
          searchQuery: state.debouncedSearchQuery,
          isReadFilter: state.isReadFilter,
          isStarredFilter: state.isStarredFilter,
          allocationFilter: state.allocationFilter,
          jobIdFilter: state.jobIdFilter,
          companyDomain: state.companyDomain,
        })
        lastWrittenFilterSigRef.current = inboxFilterSignature(next)
        return next
      },
      { replace: true },
    )
  }, [
    state.selectedCategories,
    state.debouncedSearchQuery,
    state.isReadFilter,
    state.isStarredFilter,
    state.allocationFilter,
    state.jobIdFilter,
    state.companyDomain,
    state.setSearchParams,
  ])
  useEffect(() => {
    const signature = inboxFilterSignature(state.searchParams)
    if (signature === lastWrittenFilterSigRef.current) return
    lastWrittenFilterSigRef.current = signature
    const parsed = parseInboxFiltersFromUrl(state.searchParams)
    state.setSelectedCategories(parsed.selectedCategories)
    state.setSearchQuery(parsed.searchQuery)
    state.setIsReadFilter(parsed.isReadFilter)
    state.setIsStarredFilter(parsed.isStarredFilter)
    state.setAllocationFilter(parsed.allocationFilter)
    state.setJobIdFilter(parsed.jobIdFilter)
    state.setCompanyDomain(parsed.companyDomain)
  }, [
    state.searchParams,
    state.setSelectedCategories,
    state.setSearchQuery,
    state.setIsReadFilter,
    state.setIsStarredFilter,
    state.setAllocationFilter,
    state.setJobIdFilter,
    state.setCompanyDomain,
  ])
  const clearAllFilters = useCallback(() => {
    state.setSelectedCategories([])
    state.setIsReadFilter(undefined)
    state.setIsStarredFilter(undefined)
    state.setAllocationFilter(undefined)
    state.setJobIdFilter(undefined)
    state.setSearchQuery('')
    state.setJobIdsIn([])
    state.setMailboxKeysExplicit([])
    state.setFromAddressContains('')
    state.setCompanyDomain('')
    state.setReceivedAtGte(undefined)
    state.setReceivedAtLte(undefined)
    state.setHasAttachments(undefined)
    state.setHasCalendarEvent(undefined)
  }, [
    state.setSelectedCategories,
    state.setIsReadFilter,
    state.setIsStarredFilter,
    state.setAllocationFilter,
    state.setJobIdFilter,
    state.setSearchQuery,
    state.setJobIdsIn,
    state.setMailboxKeysExplicit,
    state.setFromAddressContains,
    state.setCompanyDomain,
    state.setReceivedAtGte,
    state.setReceivedAtLte,
    state.setHasAttachments,
    state.setHasCalendarEvent,
  ])

  return {
    isNarrow, inboxSide, readerExpanded, toggleReaderExpanded, twoPaneRef,
    onSplitterPointerDown, effectiveListWidth, markupSrc, setMarkupSrc,
    openMarkup, handleClassifyUnprocessed, handleRecategorize,
    detailBodyLoading, detailFetchError, refetchDetail, selectedEmail,
    patchSelectedEmail,
    clearAllFilters,
  }
}
